import { startsWithAny } from '../../utils/startsWithAny'
import { P, match } from 'ts-pattern'

export const FILTER_SEPARATOR = '::'

// This is temporarly copied from QueryParamBuilder to avoid issues with vitest
export type FilterMenuInput = {
  name: string
  type?: 'string' | 'object' | 'array'
  value?: string | FilterMenuInput[]
  paramName?: string
  icon?: React.ReactNode
}

function extractKeywords(filter: FilterMenuInput): string[] {
  const { value, paramName } = filter
  if (Array.isArray(value)) {
    return value.flatMap((f) => extractKeywords(f))
  }
  return paramName ? [paramName] : []
}

function getKeywordType(filters: FilterMenuInput[], keyword?: string): 'string' | 'object' | 'array' | undefined {
  if (!keyword) {
    return 'string'
  }

  for (const filter of filters) {
    if (Array.isArray(filter.value)) {
      const type = getKeywordType(filter.value, keyword)
      if (type) {
        return type
      }
    }

    if (filter.paramName === keyword) {
      return filter.type ?? 'string'
    }
  }
  return undefined
}

export function parseSearchString(searchString: string, filters: FilterMenuInput[]) {
  const searchKeywords = filters.flatMap(extractKeywords)
  const { parsedParams } = searchString.split(' ').reduce(
    (acc, curr) => {
      if (
        startsWithAny(
          curr,
          searchKeywords.map((k) => `${k}${FILTER_SEPARATOR}`)
        )
      ) {
        const [keyword, ...value] = curr.split(FILTER_SEPARATOR)
        if (!keyword) {
          acc.search.push(curr)
          return acc
        }
        const type = getKeywordType(filters, keyword)
        if (type === 'array') {
          acc.parsedParams[keyword as string] = [
            ...((acc.parsedParams[keyword as string] ?? []) as string[]),
            value.join(FILTER_SEPARATOR),
          ]
        } else if (type === 'object') {
          const [key, ...rest] = value
          acc.parsedParams[keyword as string] = {
            ...((acc.parsedParams[keyword as string] ?? {}) as Record<string, string>),
            [key as string]: rest.join(FILTER_SEPARATOR),
          }
        } else {
          acc.parsedParams[keyword as string] = value.join(FILTER_SEPARATOR)
        }
      } else {
        acc.search.push(curr)
      }
      return acc
    },
    { parsedParams: {}, search: [] } as {
      parsedParams: Record<string, string | string[] | Record<string, string>>
      search: string[]
    }
  )
  return { parsedParams }
}

export function parseParams(
  queryParams: Record<string, number | string | string[] | Record<string, string> | boolean>
) {
  return Object.entries(queryParams)
    .map(([key, value]) => {
      return match(value)
        .with(P.array(), (stringArray) => stringArray.map((v) => `${key}${FILTER_SEPARATOR}${v}`).join(' '))
        .with(P.string, (v) => `${key}${FILTER_SEPARATOR}${v}`)
        .otherwise((paramRecord) => {
          return Object.entries(paramRecord as any)
            .map(([k, v]) => `${key}${FILTER_SEPARATOR}${k}${FILTER_SEPARATOR}${v}`)
            .join(' ')
        })
    })
    .join(' ')
}
